import BaseDispatcher from "@/utils/presenters/BaseDispatcher.ts";
import {Component, ComputedRef, InjectionKey, Ref, ShallowRef} from "vue";
import NamaPlace from "@/utils/presenters/NamaPlace.ts";
import ReportLauncherPlace from "@/utils/presenters/ReportLauncherPlace.ts";
import ReportViewerPlace from "@/utils/presenters/ReportViewerPlace.ts";
import FieldWidgetUtil from "@/utils/editView/widgets/FieldWidgetUtil.ts";
import {EditPresenter} from "@/utils/presenters/EditPresenter.ts";
import TreeViewPresenter from "@/utils/presenters/TreeViewPresenter.ts";
import HierarchyFilterHelper from "@/utils/common/HierarchyFilterHelper.ts";
import TreeModelProvider from "@/utils/common/TreeModelProvider.ts";
import {CommonDialogAction} from "@/utils/forms/DialogHandler.ts";
import EditPlace from "@/utils/presenters/EditPlace.ts";
import {EditViewState} from "@/utils/editView/EditViewUtils.ts";
import {FieldToFocusSearchItem} from "@/utils/common/TopPanelSearchUtils.ts";
import {RouteLocationNormalized} from "vue-router";
import {ColumnFilter, FilterHandler, IDateFilterParams, INumberFilterParams, ITextFilterParams} from "ag-grid-community";
import DashboardPresenter from "@/utils/presenters/DashboardPresenter.ts";
import {GridApi} from "ag-grid-enterprise";
import ActionPerformerHelper from "@/utils/moreMenu/ActionPerfomerHelper.ts";
import {UIMenuGroup, UIMenuItem, UIMenuSubGroup} from "@/utils/UIConstants/UIMenuGroup.ts";
import {BroadcastMessageType, TooltipContext} from "@/utils/UIConstants/enums.ts";
import QuestionField = namaTypes.QuestionField;
import BasicFieldMetaData = namaTypes.BasicFieldMetaData;
import FieldMetaData = namaTypes.FieldMetaData;
import NaMaMetaData = namaTypes.NaMaMetaData;
import DTOCriteria = namaTypes.DTOCriteria;
import FieldActionContextInfo = namaTypes.FieldActionContextInfo;
import EditScreenReport = namaTypes.EditScreenReport;
import DTOFilledQuestionField = namaTypes.DTOFilledQuestionField;
import CriteriaField = namaTypes.CriteriaField;
import BaseEntityDTO = namaTypes.BaseEntityDTO;
import EditScreenGroup = namaTypes.EditScreenGroup;
import EditScreenGrid = namaTypes.EditScreenGrid;
import EditScreenDashBoard = namaTypes.EditScreenDashBoard;
import EditScreenList = namaTypes.EditScreenList;
import EditScreenBlock = namaTypes.EditScreenBlock;
import DTOUserDashbordChartLine = namaTypes.DTOUserDashbordChartLine;
import TitledID = namaTypes.TitledID;
import EditScreenEmbeddedEdit = namaTypes.EditScreenEmbeddedEdit;
import DTOMenuItem = namaTypes.DTOMenuItem;
import DTOTimelineData = namaTypes.DTOTimelineData;
import DTOVueGUITheme = namaTypes.DTOVueGUITheme;
import DTOChatParticipant = namaTypes.DTOChatParticipant;
import DTOChatMessage = namaTypes.DTOChatMessage;
import DTOChatConversation = namaTypes.DTOChatConversation;
import TypingIndicatorDTO = namaTypes.TypingIndicatorDTO;
import GUIActionResult = namaTypes.GUIActionResult;
import DTOTabularResult = namaTypes.DTOTabularResult;
import SuggestionProvidedItem = namaTypes.SuggestionProvidedItem;
import DTOLargeData = namaTypes.DTOLargeData;
import DTOFileAttachmentInfo = namaTypes.DTOFileAttachmentInfo;
import NaMaError = namaTypes.NaMaError;
import ChatMessageSearchResult = namaTypes.ChatMessageSearchResult;
import FieldTooltipInfo = namaTypes.FieldTooltipInfo;

export interface WidgetsPresenter {
  get entityType(): string;

  isValidFieldForAttach(field: namaTypes.CompositeEditScreenField | namaTypes.EditScreenField | namaTypes.GUIActionPlaceholder): boolean;

  getDefaultCriteria(fieldId?: string): DTOCriteria | undefined;

  fetchNodeIdFromPlace(): string | undefined;

  setTreeView(treeViewPresenter: TreeViewPresenter): void;

  onTreeNodeSelection(): void;

  updateSelectedNodeId(value: string | undefined | null): void;

  getHelperForTreeView(): HierarchyFilterHelper | undefined;

  getTreeModelProviderForTreeView(): TreeModelProvider | undefined;

  fetchRecord(): Record<string, any>;

  fetchFieldErrorMsg(fieldId: string): string;

  fetchMetadata(): NaMaMetaData;

  isFieldDisabled(fieldId: string): boolean;

  isColumnDisabled(rowId: string, parentDetailId: string, columnId: string): boolean;

  isFieldHidden(field: string): boolean;

  fetchPlace(): NamaPlace | undefined;

  fetchDispatcher(): BaseDispatcher;

  createSearchContextInfo(fieldId: string, lineNumber?: number): FieldActionContextInfo ;

  fetchQuestionFields(): QuestionField[];

  fetchExtraCriteriaIdFromPlace(): string | undefined;

  fetchParentFieldTitle(): string;

  fetchCriteriaFromPlace(): DTOCriteria | undefined;

  shortcutsHandler?: (businessFunctions: string[]) => Promise<boolean>;

  isBulkUpdating: boolean;

  isEditPresenter(): this is EditPresenter;

  fetchFieldState(fieldId: string): string | undefined | null;

  fetchColumnState(rowId: string, parentDetailId: string, columnId: string): string | undefined | null;

  doesFieldHasSuggestionProvider(fieldMetaData: FieldMetaData): boolean;

  fetchCriteriaFromFilterField(filteredType?: string,fieldId?:string): DTOCriteria | undefined;

  closeQuickEditDialog?: () => void;
}

export interface WidgetProps {
  presenter: WidgetsPresenter,
  field: namaTypes.EditScreenField | namaTypes.CompositeEditScreenField | namaTypes.GUIActionPlaceholder | QuestionField,
  fieldMetaData: FieldMetaData | BasicFieldMetaData,
  doNotApplyThemeLayouts?: boolean,
  forceEnableField?: boolean,
  isInsideComposite?: boolean,
  isSpanned?: boolean,
  customPlaceHolder?: string,
  showAsPassword?: boolean,
}

export interface EditViewLayoutProps {
  presenter: EditPresenter,
  place: EditPlace,
  showTitle: boolean,
  states?: EditViewState[],
  pageIndex?: number,
  alwaysHideLabels?: boolean
}

export interface EditViewToolbarProps {
  presenter: EditPresenter,
  alwaysHideLabels?: boolean
}

export interface BlockProps {
  block: EditScreenGroup | EditScreenGrid | EditScreenDashBoard | EditScreenList | EditScreenReport | EditScreenEmbeddedEdit | EditScreenBlock,
  presenter: EditPresenter,
  blockIndex: number,
  pageIndex: number,
  collapsed: boolean,
  showMainFile?: boolean
}

export interface EmbeddedEditView {
  block: EditScreenEmbeddedEdit,
  fetchContainerObject?: () => BaseEntityDTO,
  handleHide?: () => void,
}

export interface QuickCreateProps {
  fieldUtil?: FieldWidgetUtil,
  isEditRecord?: boolean,
  newObject?: BaseEntityDTO,
  tabId?: string,
  contextInfo?: FieldActionContextInfo,
  requesterType?: string,
  afterFetchCallback?: (object: BaseEntityDTO) => void,
  isFieldEnabled?: boolean,
  parentFieldTitle?: string
}

export interface SocialUrl {
  facebook?: string,
  twitter?: string,
  youtube?: string,
  linkedin?: string,
  instagram?: string,
  snapchat?: string,
  tiktok?: string
}

export interface AboutTable {
  header: string,
  label?: string | Ref<string>,
  websiteLink?: string,
  phoneNumbers?: string[],
  email?: string,
  address?: string,
  addressLink?: string,
  socials?: SocialUrl
}

export interface FilterWidgetProps extends WidgetProps
{
  isGridColumnFilterField?: boolean,
}

export interface ReferenceWidgetProps extends FilterWidgetProps
{
  defaultEntityType?: Ref<string | null | undefined>,
  isExtraCriteria?: boolean
}

export interface DashboardWidgetProps {
  chartLine: DTOUserDashbordChartLine,
  dashboardId?: string,
  isThreeD?: boolean,
  chartKey?: string,
  isScheduler?: boolean
}

export interface ReportPopupContainer {
  block: EditScreenReport,
  presenter?: WidgetsPresenter,
  currentMode: Ref<"view" | "launch">;
  place?: ReportLauncherPlace | ReportViewerPlace,
}

export interface QuestionFieldsWithValues {
  questionFields: QuestionField[];
  defaultValues: DTOFilledQuestionField[];
}

export interface CustomDTOExpression extends namaTypes.DTOExperssion
{
  id?: number
}

export interface NaMaTreeNode {
  id?: string;
  code?: string;
  isLeaf?: boolean;
  children?: NaMaTreeNode[];
  parentId?: string;
}

export interface ListDialogProps {
  presenter: WidgetsPresenter,
  questionField: QuestionField,
  referencedEntityType?: string,
  extraButtons?: CommonDialogAction[],
  clickEvent?: Event
}

export type FilterField = QuestionField | CriteriaField;

export interface TitledIDWithIcon extends TitledID {
  iconCode?: string
}

export interface SerialRange {
  first: string,
  second: string
}

export interface BaseItem {
  label: string,
  value: string
}

export interface NamaIconProps {
  block?: TitledIDWithIcon,
  menuItem?: DTOMenuItem,
  presenter?: WidgetsPresenter,
  enumTypeName?: string,
  constant?: string,
  allowedValue?: string,
  defaultIcon?: string,
  hasColor?: boolean,
  timeline?: DTOTimelineData,
  colored?: boolean,
  defaultColor?: string
}

export interface FieldValue {
  id?: string;
  value?: string;
  rowNumber?: string;
  objectValue?: Record<string, any>;
}

export interface DashboardElement {
  id: string;
  row: number;
  column: number;
  rowSpan: number;
  columnSpan: number;
  title: string;
  widgetCode?: string;
  widgetName1?: string;
  widgetName2?: string;
}

export interface DashboardDialogPayload {
  elements: DashboardElement[];
  row: number;
  column: number;
  rowsCount: number;
}

export interface GridEventBusInfo {
  moveToNextRow?: number;
  focusedCellId?: string;
  refreshCells?: { nonce: number; columns?: string[] };
}

export interface UserMenuItem {
  titleId?: string,
  handler?: (e?: PointerEvent) => void,
  defaultIcon?: string,
  vModel?: Ref<boolean> | boolean,
  vModelStr?: Ref<string> | string,
  hasDivider?: boolean,
  theme?: DTOVueGUITheme
}

export type StringOrUndefined = string | undefined

export const widgetPageIndexKey = Symbol() as InjectionKey<number>
export const allFieldsKey = Symbol() as InjectionKey<Ref<FieldToFocusSearchItem[] | undefined>>
export const isPageChangedKey = Symbol() as InjectionKey<Ref<boolean>>

export interface RecentVisit {
  route: RouteLocationNormalized,
  count: number,
  title?: string,
  date: Date
}

export type NamaAGFilter = NamaAGColDefFilter | ColumnFilter | FilterHandler
export type NamaAGColDefFilter = {
  filter?: NamaAGFilter,
  filterParams?: Partial<NamaAGColDefFilterParams>
}

export interface NamaAGColDefFilterParams
  extends ITextFilterParams, IDateFilterParams, INumberFilterParams {
  filters?: NamaAGColDefFilter[],
  applyMiniFilterWhileTyping?: boolean,
  fieldType?: string,

  [key: string]: any
}

export interface RecentVisitGroupedByPeriod {
  lastDay: RecentVisit[],
  lastDayBefore: RecentVisit[],
  olderDays: RecentVisit[]
}

export interface DashboardProps {
  presenter: DashboardPresenter,
  isEditScreen?: boolean,
  isReportType?: boolean
}

export interface ToolbarAction {
  component?: Component,
  defaultIcon?: string,
  classProps?:(Record<string, boolean>|string)[],
  businessFunc: string,
  onClick?: () => void,
  title?: string,
  checkIfEnabled?: (a: ToolbarAction) => boolean,
  checkIfVisible?: (a: ToolbarAction) => boolean,
  class?: string
}

export const reportIsDashboardTypeKey = Symbol() as InjectionKey<ComputedRef<boolean>>
export const tableWidgetGridApiKey = Symbol() as InjectionKey<ShallowRef<GridApi>>
export const dashboardPresenterKey = Symbol() as InjectionKey<DashboardPresenter>

export interface SQLResult {
  list: (Record<string, any>)[],
  columnTypes: (Record<string, any>)[]
}

export interface ActionInProgress {
  helper: ActionPerformerHelper,
  row: number
}

export interface ProgressAction {
  title?: string,
  icon: string,
  isActionReady: boolean
}

export interface ChatMetadata extends DTOChatConversation {
  imageSrc?: string,
  participants?: DTOChatParticipant[],
  unreadMessagesCount?: number,
  chatTitle?: string,
  chatParticipantsTitle?: string,
  currentUserRole?: string
}

export interface MessageMetadata extends DTOChatMessage {
  isTemporary?: boolean,
  actualStatus?: string,
  isOwn?: boolean,
  senderName?: string,
  formattedTime?: string,
}

export interface ChatDisplayItem {
  chat: ChatMetadata;
  snippet?: ChatMessageSearchResult;
}

export interface PresenceUpdate {
  userId: string,
  online: boolean,
}

export interface MessageStatusUpdate {
  messageId: string,
  status: string,
  readAt?: string,
  deliveredAt?: string,
}

export type ClassToValues<Class> = Class[keyof Class]

export const presenterKey = Symbol() as InjectionKey<WidgetsPresenter>;

export const sideBarContext = Symbol() as InjectionKey<{
  isCurrent?(menu: UIMenuItem): boolean,
  goToEntity?(menu: UIMenuItem, shouldOpenInNewTab?: boolean): void,
  mainGroupClick?(group: UIMenuGroup): void,
  onSubGroupClicked?(subGroup: UIMenuSubGroup): void,
  searchValue: Ref<string | null>,
  panel: Ref<string>,
  currentGroup: Ref<UIMenuGroup | null>,
  currentSubGroup: Ref<UIMenuSubGroup | null>,
  displayedMenuGroups: ComputedRef<UIMenuGroup[]>,
  searchMenuGroups: ComputedRef<UIMenuGroup[]>,
}>

// WebSocket task progress payload (server pushes every 500ms for active tasks)
export interface TaskProgressPayload {
  results: namaTypes.GUIActionResult[];
  resultsCount: number;
}

export interface WsChatMessagePayload {
  message: MessageMetadata;
}

export interface WsStatusUpdatePayload {
  statusUpdate: MessageStatusUpdate;
}

export interface WsPresencePayload {
  presenceUpdate: PresenceUpdate;
}

export interface WsTypingPayload {
  indicator: TypingIndicatorDTO;
}

export interface WsSendMessagePayload {
  conversationId: string;
  messageContent: string;
  messageType?: string;
  attachment?: DTOLargeData;
  id?: string;
}

export interface WsSendTypingPayload {
  conversationId: string;
  typing: boolean;
}

export interface WsSubscribeTypingPayload {
  conversationId: string;
}

export interface WsReceiptPayload {
  messageId: string;
}

export interface WsNotificationsAndApprovalsPayload {
  count: number;
  result: DTOTabularResult[] | null;
}

export interface WsCountsPayload {
  approvalsCount: number;
  notificationsCount: number;
}

export interface WsTasksPayload {
  tasks: GUIActionResult[];
  count: number;
}

export interface WsChatUnreadPayload {
  count?: number;
  refresh?: boolean
}

export interface BroadcastMessage<T = any> {
  type: BroadcastMessageType;
  payload: T;
  senderId: string;
  timestamp: number;
}

// Payload types for type safety
export interface ChatMessagePayload {
  message: MessageMetadata;
  conversationId?: string;
}

export interface ChatUnreadCountPayload {
  conversationId: string;
  count: number;
  totalUnread: number;
}

export interface NotificationPayload {
  notifications: namaTypes.DTOTabularResult[];
  count: number;
}

export interface ApprovalPayload {
  approvals: namaTypes.DTOTabularResult[];
  count: number;
}

export interface TaskPayload {
  tasks: namaTypes.GUIActionResult[];
  count: number;
}

export interface CountUpdatePayload {
  count: number;
}

export interface ChatAttachment extends DTOFileAttachmentInfo {
  fileSize: number;
}

export interface SuggestionProviderItemsWithOptions extends SuggestionProvidedItem {
  optionText?: string,
  optionDisplayCode?: string,
  optionCaption?: string,
  optionDirection?: string,
  optionHasColorOrImg?: boolean,
  colorCode?: string
}

export interface DisplayEntry {
  type: 'single' | 'album';
  messages: MessageMetadata[];
}

export interface tooltipParams {
  entityType: string,
  field: string,
  fallBackId: string,
  lineNumber: number,
  automaticTooltipsOnly: boolean,
  context: TooltipContext,
  url: string,
  buildRequest: (tooltips: FieldTooltipInfo[]) => any
}


export interface NamaWarning extends NaMaError {
  warning?: boolean;
}


export interface URLSerializer<T> {

  flattenIfNeeded(items?: T[]): T[] | undefined;

  toProperties(item: T): Map<string, string>;

  fromProperties(properties: Map<string, string>): T;

  toURL(items: any): string;

  fromURL?(url: string | undefined): T[];
}
